import { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  accordionSummaryClasses,
  Box,
  Paper,
  styled,
  Typography,
} from '@mui/material';

import { Hint } from './Hint';
import { Icon } from './Icon';

const StyledAccordion = styled(Accordion)`
  background: transparent;

  &:before {
    display: none;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  border-bottom: thin solid ${({ theme }) => theme.palette.divider};
  padding: 0;
  min-height: none !important;
  font-weight: bold;
  min-height: 32px;

  &:hover {
    color: ${({ theme }) => theme.palette.secondary.main};
    border-bottom-color: ${({ theme }) => theme.palette.secondary.main};

    .${accordionSummaryClasses.expandIconWrapper} {
      color: ${({ theme }) => theme.palette.secondary.main};
    }
  }

  .${accordionSummaryClasses.content} {
    align-items: center;
    margin: 0;
    display: inline-flex;
    gap: ${({ theme }) => theme.spacing()};
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: ${({ theme }) => theme.spacing(1, 0)};
`;

export type ContentAccordionProps = PropsWithChildren<
  Omit<AccordionProps, 'disableGutters'> & {
    dense?: boolean;
    disablePaper?: boolean;
    displayCheckIcon?: boolean;
    error?: boolean;
    title: string;
  }
>;

export const ContentAccordion: FC<ContentAccordionProps> = ({
  children,
  dense,
  disablePaper,
  displayCheckIcon,
  error,
  title,
  elevation = 0,
  ...rest
}) => {
  const { t } = useTranslation();

  return (
    <StyledAccordion disableGutters elevation={elevation} {...rest}>
      <StyledAccordionSummary>
        <Typography component='h2' variant='inherit'>
          {title}
        </Typography>

        {displayCheckIcon && <Hint color='success' icon='doubleCheck' tooltip={t('hasContent')} />}

        {error && <Icon color='error' fontSize='inherit' name='error' />}
      </StyledAccordionSummary>

      <StyledAccordionDetails>
        {disablePaper ? (
          children
        ) : (
          <Box component={Paper} p={dense ? undefined : 2}>
            {children}
          </Box>
        )}
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};
